import React, { useEffect, useState } from 'react';
import { FarmerService } from '../services/FarmerService';
import './RawFruit.css';

// 1) Define the shape of a Farmer based on your columns
interface Farmer {
  id: string;               // primary key
  name: string;
  location: string;
  aadharNumber: string;
  aadharFront: string | null;
  aadharBack: string | null;
  bankDetail: string | null;
  farmersImpacted: string | null;
  collectionSource: string | null;
}

const FarmerPage: React.FC = () => {
  // 2) Farmer list state
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 3) Form controls
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // 4) Form input states
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [aadharNumber, setAadharNumber] = useState('');
  const [aadharFront, setAadharFront] = useState('');
  const [aadharBack, setAadharBack] = useState('');
  const [bankDetail, setBankDetail] = useState('');
  const [farmersImpacted, setFarmersImpacted] = useState('');
  const [collectionSource, setCollectionSource] = useState('');

  // 5) Validation errors
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    location?: string;
    aadharNumber?: string;
  }>({});

  // ----------------------------------------
  // Fetch all farmers on mount
  // ----------------------------------------
  const fetchFarmers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await FarmerService.fetchFarmers();
      setFarmers(response.data);
    } catch (err) {
      console.error('Error fetching farmers:', err);
      setError('Failed to load farmers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFarmers();
  }, []);

  // ----------------------------------------
  // Handle form submission
  // ----------------------------------------
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitting(true);

    // Clear old errors
    setFormErrors({});

    // Basic validations
    const errors: {
      name?: string;
      location?: string;
      aadharNumber?: string;
    } = {};

    if (!name.trim()) {
      errors.name = 'Name is required';
    }
    if (!location.trim()) {
      errors.location = 'Location is required';
    }
    if (!aadharNumber.trim()) {
      errors.aadharNumber = 'Aadhar Number is required';
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setFormSubmitting(false);
      return;
    }

    // Prepare data for the backend
    const newFarmerData = {
      name: name.trim(),
      location: location.trim(),
      aadharNumber: aadharNumber.trim(),
      aadharFront: aadharFront.trim() || null,
      aadharBack: aadharBack.trim() || null,
      bankDetail: bankDetail.trim() || null,
      farmersImpacted: farmersImpacted.trim() || null,
      collectionSource: collectionSource.trim() || null,
    };

    try {
      await FarmerService.createFarmer(newFarmerData);
      alert('Farmer created successfully!');

      // Reset form
      setName('');
      setLocation('');
      setAadharNumber('');
      setAadharFront('');
      setAadharBack('');
      setBankDetail('');
      setFarmersImpacted('');
      setCollectionSource('');

    } catch (err) {
      console.error('Error creating farmer:', err);
      setError('Failed to create farmer');
    } finally {
        // Hide form
      setIsFormVisible(false);
      setFormSubmitting(false);
      // Refresh the list
      fetchFarmers();
    }
  };

  // ----------------------------------------
  // Render
  // ----------------------------------------
  return (
    <div className="page">
        <div className="container">
      <div className="header">
        <button
          className="button"
          style={{ position: 'absolute', top: '10px', right: '10px' }}
          onClick={() => setIsFormVisible(true)}
        >
          Add Farmer
        </button>
      </div>

      {loading ? (
        <p>Loading farmers...</p>
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Location</th>
              <th>Aadhar Number</th>
              <th>Aadhar Front</th>
              <th>Aadhar Back</th>
              <th>Bank Detail</th>
              <th>Farmers Impacted</th>
              <th>Collection Source</th>
            </tr>
          </thead>
          <tbody>
            {farmers.map((farmer) => (
              <tr key={farmer.id}>
                <td>{farmer.name}</td>
                <td>{farmer.location}</td>
                <td>{farmer.aadharNumber}</td>
                <td>{farmer.aadharFront || 'N/A'}</td>
                <td>{farmer.aadharBack || 'N/A'}</td>
                <td>{farmer.bankDetail || 'N/A'}</td>
                <td>{farmer.farmersImpacted || 'N/A'}</td>
                <td>{farmer.collectionSource || 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isFormVisible && (
        <div className="form-popup">
          <form onSubmit={handleSubmit} className="form">
            <h2>Add a New Farmer</h2>

            <label className="label">Name:</label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {formErrors.name && <p className="error">{formErrors.name}</p>}

            <label className="label">Location:</label>
            <input
              type="text"
              className="input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
            {formErrors.location && (
              <p className="error">{formErrors.location}</p>
            )}

            <label className="label">Aadhar Number:</label>
            <input
              type="text"
              className="input"
              value={aadharNumber}
              onChange={(e) => setAadharNumber(e.target.value)}
            />
            {formErrors.aadharNumber && (
              <p className="error">{formErrors.aadharNumber}</p>
            )}

            <label className="label">Aadhar Front:</label>
            <input
              type="text"
              className="input"
              value={aadharFront}
              onChange={(e) => setAadharFront(e.target.value)}
            />

            <label className="label">Aadhar Back:</label>
            <input
              type="text"
              className="input"
              value={aadharBack}
              onChange={(e) => setAadharBack(e.target.value)}
            />

            <label className="label">Bank Detail:</label>
            <input
              type="text"
              className="input"
              value={bankDetail}
              onChange={(e) => setBankDetail(e.target.value)}
            />

            <label className="label">Farmers Impacted:</label>
            <input
              type="text"
              className="input"
              value={farmersImpacted}
              onChange={(e) => setFarmersImpacted(e.target.value)}
            />

            <label className="label">Collection Source:</label>
            <input
              type="text"
              className="input"
              value={collectionSource}
              onChange={(e) => setCollectionSource(e.target.value)}
            />

            <button type="submit" className="button" disabled={formSubmitting}>
              {formSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            <button
              type="button"
              className="button cancel"
              onClick={() => setIsFormVisible(false)}
            >
              Cancel
            </button>
          </form>
        </div>
      )}
      </div>
    </div>
  );
};

export default FarmerPage;
